import { useEffect, useMemo, useRef, useState } from 'react'
import { faSearch, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { Icon, Select, SelectDropdownOption, Badge } from '../../..'
import { matchesOptionSearch } from '../../../utils/option-search'
import { mtc } from '../../../utils/style-helpers'

export interface MultiselectProps<T extends string> {
  options: SelectDropdownOption<T>[]
  value: T[]
  onChange: (value: T[]) => void
  disabled?: boolean
  onBlur?: () => void
  placeholder?: string
  searchable?: boolean
  /** Keep the trigger a fixed single-row height no matter how many chips
   *  are selected — they scroll horizontally instead of wrapping and
   *  growing the box taller. Defaults to false (wrap + grow), the
   *  original behavior every other caller already relies on. */
  fixedHeight?: boolean
}

export const Multiselect = <T extends string>({
  value,
  options,
  onChange,
  disabled,
  onBlur,
  placeholder,
  searchable = false,
  fixedHeight = false,
}: MultiselectProps<T>) => {
  const [searchValue, setSearchValue] = useState('')
  // Select has trouble with its open/closed state management when making selections so it must be manually handled
  const [open, setOpen] = useState(false)
  // No option is ever the focused element, so the highlight needs state.
  const [activeIndex, setActiveIndex] = useState(0)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  const shownOptions = useMemo(
    () =>
      options.filter((option) =>
        matchesOptionSearch(searchValue, option.text, option.searchText)
      ),
    [options, searchValue]
  )

  // `nearest`: only scroll an option that is actually out of view.
  useEffect(() => {
    if (!open) return
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open])

  const toggle = (optionValue: T | undefined, onClick?: () => void): void => {
    if (optionValue !== undefined) {
      if (value.includes(optionValue)) {
        onChange(value.filter((val) => val !== optionValue))
      } else {
        onChange([...value, optionValue])
      }
    }
    setSearchValue('')
    onClick?.()
  }

  /**
   * Radix's own key handling assumes the trigger holds focus; here the search
   * input inside it does, so none of it runs. `stopPropagation` keeps the
   * trigger underneath from also acting on a claimed key. Enter toggles and
   * leaves the list open, since this picks several options in a row.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // Otherwise the caret jumps to either end of the search text.
      e.preventDefault()
      e.stopPropagation()
      if (!open) {
        setOpen(true)
        setActiveIndex(0)
        return
      }
      if (shownOptions.length === 0) return
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex(
        (i) => (i + delta + shownOptions.length) % shownOptions.length
      )
      return
    }

    if (e.key === 'Enter') {
      if (!open) return
      // A bare Enter would submit the surrounding form, empty results
      // included, so the key is claimed whenever the list is open.
      e.preventDefault()
      e.stopPropagation()
      const option = shownOptions[activeIndex]
      if (option) toggle(option.value, option.onClick)
      return
    }

    if (e.key === 'Escape' && open) {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <Select open={open} disabled={disabled} onBlur={onBlur}>
      <Select.Trigger
        onClick={() => setOpen((prev) => !prev)}
        className={mtc(
          'multi-select-trigger min-h-[40px]',
          fixedHeight ? 'h-10' : 'h-auto'
        )}
      >
        {searchable && <Icon icon={faSearch} />}
        <div
          className={mtc(
            'flex flex-1 ml-2 gap-y-2',
            fixedHeight
              ? 'flex-nowrap items-center overflow-x-auto'
              : 'flex-wrap'
          )}
        >
          {value.map((v) => (
            <Badge
              key={v}
              color="olive"
              variant="secondary"
              className={mtc('mr-2 cursor-default', fixedHeight && 'shrink-0')}
              onClick={(e) => e.stopPropagation()}
            >
              {options.find((option) => option.value === v)?.text}
              <Icon
                icon={faXmark}
                onClick={() => onChange(value.filter((val) => val !== v))}
                color="text-moss-200"
                className="cursor-pointer pl-2"
              />
            </Badge>
          ))}

          <div
            role="presentation"
            className={fixedHeight ? 'shrink-0' : undefined}
            onClick={(e) => {
              // Prevent parent from getting on click
              e.stopPropagation()
            }}
          >
            <input
              placeholder={placeholder || 'Search'}
              className={mtc(
                'w-[110px] h-[30px] outline-none text-neutral-600'
              )}
              value={searchValue}
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                setSearchValue(e.target.value)
                setOpen(true)
                setActiveIndex(0)
              }}
              onBlur={(e) => {
                // The select parent is attempting to eat all of the events on the child, not sure how or why they are
                // bubbling up but it has something to do with radix's implementation of select.  This is a hack to
                // prevent that and keep the focus on the search bar
                if (
                  e.relatedTarget?.classList.contains('multi-select-content') ||
                  e.relatedTarget?.classList.contains(
                    'multi-select-content-item'
                  ) ||
                  e.relatedTarget?.classList.contains('multi-select-trigger')
                ) {
                  e.currentTarget.focus()
                } else {
                  // Clear search if user clicks away
                  setSearchValue('')
                }
              }}
              onClick={(e) => {
                e.currentTarget.focus()
                setOpen(true)
              }}
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content
        onPointerDownOutside={() => setOpen(false)}
        className="multi-select-content"
      >
        {shownOptions.map(
          ({ key, value: optionValue, text, onClick }, index) => (
            <Select.Item
              // The atom's own hover colour, so arrowing and mousing match.
              className={mtc(
                'multi-select-content-item',
                index === activeIndex && 'bg-neutral-25'
              )}
              ref={(el: HTMLDivElement | null) => {
                optionRefs.current[index] = el
              }}
              value={optionValue ?? ''}
              key={key || optionValue}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => toggle(optionValue, onClick)}
              data-state={
                optionValue !== undefined && value.includes(optionValue)
                  ? 'checked'
                  : 'unchecked'
              }
            >
              {text}
            </Select.Item>
          )
        )}
      </Select.Content>
    </Select>
  )
}
